import { cartChangePush, cartSingleItemPush } from "../../utils/functions/dataLayerPush";
import * as storeService from "./storeService";
import { Action, CartPayload } from "../../utils/types/states";
import React, { Dispatch } from "react";
import { Cart, Order } from "../../utils/types/dto";
import { getOrder } from "./storeService";
import { IAPIError } from "@on/model-store";
import { isApiError } from "./commonService";
import { translate, TranslateParamsKeyOnly } from "../../utils/functions/translationComponent";
import { isPromotionAppliedOrApplicable } from "../promotion/renderPromotions";
import { translateApiError } from "../../utils/functions/translation";
import { getUrlParams } from "../../utils/functions/common";
import { MediaImage, ProductMediaOf } from "../../utils/types/graphQLTypes";
import { bundleImages, mainProductImage } from "../../utils/functions/productImages";

function pollQueryStringParams() {
  const queryStringParams = getUrlParams();
  if (queryStringParams) {
    const cartRecoveryToken = queryStringParams.get("cart_recovery_token") ?? undefined;
    const registrationConfirmationToken = queryStringParams.get("registration_confirmation_token") ?? undefined;
    const smsToken = queryStringParams.get("sms") ?? undefined;
    const messageCode = queryStringParams.get("message_code") ?? undefined;
    const errorMessage = queryStringParams.get("error_message") ?? undefined;

    if (cartRecoveryToken) {
      queryStringParams.delete("cart_recovery_token");
    }
    if (registrationConfirmationToken) {
      queryStringParams.delete("registration_confirmation_token");
    }
    if (smsToken) {
      queryStringParams.delete("sms");
    }
    if (messageCode) {
      queryStringParams.delete("message_code");
    }
    if (errorMessage) {
      queryStringParams.delete("error_message");
    }

    if (cartRecoveryToken || registrationConfirmationToken || smsToken || errorMessage) {
      const urlWithoutQueryString = window.location.href.split("?")[0];
      const newUrl = `${urlWithoutQueryString}?${queryStringParams.toString()}`;
      window.history.replaceState({}, document.title, newUrl);
    }
    return { cartRecoveryToken: cartRecoveryToken || smsToken, registrationConfirmationToken, messageCode: messageCode as TranslateParamsKeyOnly | undefined, errorMessage };
  }
  return { cartRecoveryToken: undefined, registrationConfirmationToken: undefined, messageCode: undefined, errorMessage: undefined };
}

export const fetchCart = async (dispatch: React.Dispatch<Action>) => {
  const { cartRecoveryToken, registrationConfirmationToken, messageCode, errorMessage } = pollQueryStringParams();

  let cart: CartPayload;
  if (registrationConfirmationToken || cartRecoveryToken) {
    const response = await storeService.getCartWithToken({ registrationConfirmationToken, cartRecoveryToken });
    if (isApiError(response)) {
      cart = await storeService.getCart();
      cart.globalMessage = {
        message: translateApiError(response),
        type: "ERROR",
      };
    } else {
      cart = response;
      cart.globalMessage = {
        message: translate({ key: messageCode ?? (registrationConfirmationToken ? "account.confirmed" : "cart.recovered") }) as string,
        type: "INFO",
      };
    }
  } else {
    cart = await storeService.getCart();
    if (errorMessage) {
      cart.globalMessage = {
        message: errorMessage,
        type: "ERROR",
      };
    }
  }

  dispatch({
    type: cartRecoveryToken ? "CART_RECOVERED" : "CART_LOADED",
    payload: cart,
  });
};

export const reloadCart = async (dispatch: React.Dispatch<Action>): Promise<void> => {
  const cart = await storeService.getCart();
  dispatch({
    type: "CART_LOADED",
    payload: cart,
  });
};

export const addToCart = async (params: { productId: string; quantity: number; subProductsReplacementForced: boolean }, dispatch: React.Dispatch<Action>): Promise<Cart | IAPIError> => {
  const cart = await storeService.addToCart({ product_id: params.productId }, params.quantity, params.subProductsReplacementForced);
  if (!isApiError(cart)) {
    if (typeof window !== "undefined") {
      cartSingleItemPush(cart, params.productId, params.quantity);
    }
    const additionalData = { productId: params.productId, quantity: params.quantity };
    dispatch({ type: "ADD_ITEM", payload: { ...cart, additionalData } });
    return cart;
  } else {
    dispatch({ type: "ADD_ITEM_FAILED", payload: { errorMessage: "Nie udało się dodać do koszyka, produkt niedostępny", isPermanent: true } });
    window.scrollTo({ top: 0, behavior: "smooth" });
    return cart;
  }
};

export const setQuantity = async (
  productId: string,
  //lineId: string,
  quantity: number,
  dispatch: React.Dispatch<Action>,
  increasing: boolean,
  before: Cart
) => {
  const currentCart = await storeService.updateItem({ product_id: productId }, quantity, increasing);
  if (typeof window !== "undefined" && !isApiError(currentCart)) {
    cartChangePush(before, currentCart, productId);
  }
  // if (!increasing) {
  //   const { cart } = await adjustGiftPackingQuantity(currentCart, lineId, quantity);
  //   currentCart = cart;
  // }
  dispatchQuantityChange(currentCart, { productId, quantity, increasing }, dispatch);
};

/** The cart after a line's quantity changed, with what changed (the same dispatch whichever call changed it). */
export const dispatchQuantityChange = (cart: Cart, change: { productId: string; quantity: number; increasing: boolean }, dispatch: React.Dispatch<Action>) => {
  dispatch({ type: "SET_QUANTITY", payload: { ...cart, additionalData: change } });
};

export const applyCoupon = async (couponCode: string, dispatch: Dispatch<Action>) => {
  const newCart = await storeService.applyCoupon(couponCode);
  if (isApiError(newCart)) {
    dispatch({ type: "APPLY_COUPON_FAILED", payload: { errorMessage: translateApiError(newCart) } });
  } else {
    dispatch({ type: "APPLY_COUPON", payload: newCart });
  }
};

export const removeCoupon = async (couponCode: string, dispatch: Dispatch<Action>) => {
  const newCart = await storeService.removeCoupon(couponCode);
  if (isApiError(newCart)) {
    // TODO: handle error
  } else {
    dispatch({ type: "REMOVE_COUPON", payload: newCart });
  }
};

export const getOrderConfirmation = async (orderId: string): Promise<Order | IAPIError> => {
  return await getOrder(orderId);
};

export const isFreeShippingAppliedOrApplicable = (cart: Cart): boolean => {
  return (
    cart.discounts.some((discount) => discount.type === "FREE_SHIPPING") ||
    cart.promotions.filter((promotion) => promotion.discountType === "FREE_SHIPPING").some((freeShippingPromo) => isPromotionAppliedOrApplicable(freeShippingPromo, cart))
  );
};

export interface CartProductMedia {
  product_id: string;
  productType: string;
  media: ProductMediaOf<MediaImage>;
}

export const getCartImageForProductId = (productId: string, allProductsMedia: CartProductMedia[]) => {
  const product = allProductsMedia.find((product) => product.product_id === productId);
  if (!product) {
    return undefined;
  }
  return product.productType === "BUNDLE" ? bundleImages(product.media)[0] : mainProductImage(product.media);
};
